import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { applyCardVisibility, CARD_OBSERVER_OPTIONS } from '../../lib/feedCardVisibility';

import FastList from '../../components/fastlist';
import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';
import ImageScroller from '../../components/imagescroller';
import VideoScroller from '../../components/videoscroller';
import FileList from '../../components/fileViewer/list';
import GradientRight from '../../components/gui/gradientRight';
import Placeholder from '../../components/gui/placeholder';

import Comments from '../../components/comments';
import Likes from '../../components/likes';
import TranslationButtons from '../../components/translation/buttons';

// C media components come from the product registry — stubbed to {} in the
// Dyrejournal export; the render sites below fall back to plain mp4 <video>.
// Access members at render time only (componentsOther.X) — the registry sits
// in an import cycle and is not fully evaluated at module scope.
import componentsOther from '../../product/components-other';

const scrollDistance = 210;
const scrollDistanceSnap = 90;
const activationRange = 20;

const MAX_HEIGHT = 210;

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    filterPlaceholder: <Text id='list.filter-placeholder'>Filter {'{title}'}... (press / to focus)</Text>,
}))
@observer
class GuiList extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            focusedIndex: 0,
            extended: {},
            noToggle: {},
            showHeart: {},
            playingVideos: {},
            activeTranslations: {}, // { [objectId]: { language: 'en', translation: {...} } }
            translationLoading: {}, // { [objectId]: 'en' } - which language is loading for which object
        };
        this.contentContainer = {};
        this.scrollViewTimer = null;
        this.searchInputField = null;
        this.videoRefs = {};
        this.videoObserver = null;
        this.cardRefs = {};
        this.cardObserver = null;
    }

    setupVideoObserver = () => {
        if (!('IntersectionObserver' in window)) {
            return;
        }
        this.videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    const articleId = entry.target.dataset.articleId;
                    if (articleId) {
                        this.stopVideo(parseInt(articleId, 10));
                    }
                }
            });
        }, { threshold: 0.1 });
    }

    stopVideo = (articleId) => {
        this.setState(prevState => {
            const newPlayingVideos = { ...prevState.playingVideos };
            delete newPlayingVideos[articleId];
            return { playingVideos: newPlayingVideos };
        });
    }

    setVideoRef = (articleId, ref) => {
        if (ref && this.videoObserver) {
            ref.dataset.articleId = articleId;
            this.videoObserver.observe(ref);
            this.videoRefs[articleId] = ref;
        } else if (!ref && this.videoRefs[articleId] && this.videoObserver) {
            this.videoObserver.unobserve(this.videoRefs[articleId]);
            delete this.videoRefs[articleId];
        }
    }

    setupCardObserver = () => {
        if (!('IntersectionObserver' in window)) {
            return;
        }
        this.cardObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => applyCardVisibility(entry.target, entry.isIntersecting));
        }, CARD_OBSERVER_OPTIONS);
    }

    setCardRef = (articleId, ref) => {
        if (ref && this.cardObserver) {
            this.cardObserver.observe(ref);
            this.cardRefs[articleId] = ref;
        } else if (!ref && this.cardRefs[articleId] && this.cardObserver) {
            this.cardObserver.unobserve(this.cardRefs[articleId]);
            delete this.cardRefs[articleId];
        }
    }

    viewElement = (e, props) => {
        // Check if the click target is an external link
        const clickedLink = e.target.closest('a');
        if (clickedLink) {
            const href = clickedLink.getAttribute('href') || '';
            // Only allow external links (http/https) to open in new tab
            // Internal links (starting with /) should open in drawer instead
            if (href.startsWith('http://') || href.startsWith('https://')) {
                clickedLink.setAttribute('target', '_blank');
                clickedLink.setAttribute('rel', 'noopener noreferrer');
                // Track link click in content
                const boxLine = e.target.closest('.box-line');
                const articleId = boxLine?.dataset?.id ? parseInt(boxLine.dataset.id, 10) : null;
                if (articleId) {
                    const { appState } = this.props.stores;
                    appState.addUsageLog({
                        type: 'feed_link_click',
                        props: {
                            articleId,
                            url: href,
                        },
                        timestamp: Math.floor(new Date().getTime() / 1000),
                    });
                }
                return;
            }
            // Internal hash-route links (/index#/... or #/...) navigate in-app via preact-router
            const hashIdx = href.indexOf('#/');
            if (hashIdx >= 0) {
                e.preventDefault();
                e.stopPropagation();
                const path = href.slice(hashIdx + 1);
                const boxLine = e.target.closest('.box-line');
                const articleId = boxLine?.dataset?.id ? parseInt(boxLine.dataset.id, 10) : null;
                if (articleId) {
                    const { appState } = this.props.stores;
                    appState.addUsageLog({
                        type: 'feed_link_click',
                        props: {
                            articleId,
                            url: href,
                        },
                        timestamp: Math.floor(new Date().getTime() / 1000),
                    });
                }
                route(path);
                return;
            }
            // For other internal links, prevent default and let drawer handle it
        }

        e.stopPropagation();
        e.preventDefault();
        const { swipeIdOpen } = this.state;
        const { id } = props ? props?.id : e?.target.closest('.box-line').dataset;
        const objId = parseInt(id, 10);
        if (swipeIdOpen === objId) {
            this.closeSwipMenu(e);
            return;
        }
        const { view = () => {} } = this.props;
        view(e, props);
    }

    deleteElement = e => {
        const { deleteElement = () => {} } = this.props;
        deleteElement(e);
        this.closeSwipMenu(e);
    }

    playVideo = (e, articleId, articleUrl) => {
        e.stopPropagation();
        e.preventDefault();
        const { appState } = this.props.stores;
        appState.addUsageLog({
            type: 'feed_video_play',
            props: {
                articleId,
                url: articleUrl,
            },
            timestamp: Math.floor(new Date().getTime() / 1000),
        });
        this.setState(prevState => ({
            playingVideos: {
                ...prevState.playingVideos,
                [articleId]: true,
            },
        }));
    }

    getYoutubeVideoId = (url) => {
        if (!url) return null;
        // Match youtube.com/watch?v=ID
        let match = url.match(/youtube\.com\/watch\?v=([^&\s]+)/);
        if (match) return match[1];
        // Match youtu.be/ID
        match = url.match(/youtu\.be\/([^?&\s]+)/);
        if (match) return match[1];
        // Match youtube.com/shorts/ID
        match = url.match(/youtube\.com\/shorts\/([^?&\s]+)/);
        if (match) return match[1];
        return null;
    }

    isYoutubeUrl = (url) => {
        if (!url) return false;
        return url.match(/youtube\.com/) || url.match(/youtu\.be/);
    }

    rentOutElement = e => {
        const { rentOutElement = () => {} } = this.props;
        rentOutElement(e);
        this.closeSwipMenu(e);
    }

    moveElement = e => {
        const { moveElement = () => {} } = this.props;
        moveElement(e);
        this.closeSwipMenu(e);
    }

    addUsageData = e => {
        const { addUsageData = () => {} } = this.props;
        addUsageData(e);
        this.closeSwipMenu(e);
    }

    checkElement = e => {
        const { checkElement = () => {} } = this.props;
        checkElement(e);
        this.closeSwipMenu(e);
    }

    editElement = e => {
        const { editElement = () => {} } = this.props;
        editElement(e);
        this.closeSwipMenu(e);
    }

    refreshElement = async e => {
        const { refreshElement = () => {} } = this.props;
        this.setState({ refreshElementInProgress: true });
        await refreshElement(e);
        this.setState({ refreshElementInProgress: false });
        this.closeSwipMenu(e);
    }

    handleIntersection = (entries) => {
        const { holdingStore } = this.props;
        if (holdingStore) {
            holdingStore.intersectionObserverCallback(entries);
        }
    }

    // Swipe shit
    touchStart = (e) => {
        const { id } = e.target.closest('.box-line').dataset;
        const { isSwipedOpen } = this.state;
        this.setState({
            swipeId: parseInt(id, 10),
            startY: e.touches[0].pageY,
            startX: e.touches[0].pageX,
            isSwipedOpen: false,
            diffY: undefined,
            diffX: undefined,
            // absDiffX: undefined,
            deleteVerify: false,
        });
    }

    touchEnd = (e) => {
        const { isSwipedOpen, absDiffX } = this.state;
        if (isSwipedOpen) {
            this.setState({
                startY: undefined,
                startX: undefined,
            });
        } else if (absDiffX >= scrollDistanceSnap) {
            const { id } = e.target.closest('.box-line').dataset;
            const swipeId = parseInt(id, 10);
            this.setState({
                isSwipedOpen: true,
                swipeIdOpen: swipeId,
                absDiffX: scrollDistance,
            });
        } else if (absDiffX >= activationRange) {
            this.setState({
                startY: undefined,
                startX: undefined,
                isSwipedOpen: false,
                deleteVerify: false,
                diffY: 1,
                diffX: 1,
                absDiffX: 1,
            });
            setTimeout(() => {
                this.setState({
                    swipeIdOpen: undefined,
                    diffY: undefined,
                    diffX: undefined,
                    absDiffX: undefined,
                });
            }, 400);
        }
    }

    touchMove = (e) => {
        // e.stopPropagation(); // This should be removed to allow pull to refresh when on top of page.
        const { isSwipedOpen } = this.state;
        if (isSwipedOpen) {
            return false;
        }
        const { startY, startX } = this.state;
        const y = e.touches[0].pageY;
        const x = e.touches[0].pageX;
        const diffY = y - startY;
        const absDiffY = Math.abs(diffY);
        let diffX = x - startX;
        let absDiffX = Math.abs(diffX);
        let swipeDirection = '';

        if ( absDiffX < absDiffY ) { /*most significant*/
            return false;
        }

        if (absDiffX < activationRange) {
            return true;
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
        if (diffX > 0) {
            swipeDirection = 'right';
        } else if (diffX < 0) {
            swipeDirection = 'left';
        }
        if (absDiffX >= scrollDistance) {
            absDiffX = scrollDistance;
        }
        // console.log({ absDiffX, scrollDistance, swipeDirection });
        this.setState({
            diffY,
            diffX,
            absDiffX,
            swipeDirection,
        });

        if (y > (startY + scrollDistance)) {
            // console.log('swiping down', y, '>', startY + scrollDistance);
        } else if (y < (startY - scrollDistance)) {
            // console.log('swiping up', y, '<', startY + scrollDistance);
        }

        if (absDiffX >= scrollDistance) {
            const { id } = e.target.closest('.box-line').dataset;
            const swipeId = parseInt(id, 10);
            this.setState({
                isSwipedOpen: true,
                swipeIdOpen: swipeId,
            });
        }
    }

    closeSwipMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState({
            diffY: 1,
            diffX: 1,
            absDiffX: 1,
        });
        setTimeout(() => {
            this.setState({
                isSwipedOpen: false,
                swipeIdOpen: undefined,
                diffY: undefined,
                diffX: undefined,
                absDiffX: undefined,
                deleteVerify: false,
            });
        }, 400);
    }
    // /Swipe shit

    clearSearch = () => {
        this.setState({
            search: '',
        });
    }

    searchInput = (e) => {
        const search = e.target.value;
        const { holdingStore, searchCallback = () => {} } = this.props;
        this.setState({ search });
        searchCallback(search);
    }

    addLike = async (type, objectId) => {
        const { likeStore } = this.props.stores;
        await likeStore.insert({ type, objectId });
        this.setState({ hasLiked: true });

        const likes = await likeStore.load({
            query: { type, objectId },
            skipUpdate: true,
        });

        const { showHeart = {} } = this.state;
        showHeart[objectId] = true;
        this.setState({ localLikes: likes, showHeart });
        // show & auto-hide the big heart
        setTimeout(() => {
            const { showHeart = {} } = this.state;
            showHeart[objectId] = false;
            this.setState({ showHeart });
        }, 800);
    }

    onDoubleClickImage = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { objectid } = e.target.dataset;
        const idInt = parseInt(objectid, 10);
        const { likeCommentType = 'article' } = this.props;
        await this.addLike(likeCommentType, idInt);
    }

    handleTranslate = async (objectId, language, objectType) => {
        const { translationLoading } = this.state;

        // Set loading state for this object
        this.setState({
            translationLoading: { ...translationLoading, [objectId]: language },
        });

        try {
            // Determine API endpoint based on object type
            const endpoint = objectType === 'news'
                ? `/api/news/${objectId}/translate`
                : `/api/articles/${objectId}/translate`;

            const response = await util.fetchApi(endpoint, { method: 'POST' }, { language });

            if (response && response.status === 200 && response.data) {
                const { activeTranslations } = this.state;
                this.setState({
                    activeTranslations: {
                        ...activeTranslations,
                        [objectId]: {
                            language,
                            translation: response.data.translation,
                            cached: response.data.cached,
                        },
                    },
                    translationLoading: { ...this.state.translationLoading, [objectId]: null },
                });
            } else {
                console.error('[Translation] Failed:', response?.message);
                this.setState({
                    translationLoading: { ...this.state.translationLoading, [objectId]: null },
                });
            }
        } catch (error) {
            console.error('[Translation] Error:', error);
            this.setState({
                translationLoading: { ...this.state.translationLoading, [objectId]: null },
            });
        }
    }

    clearTranslation = (objectId) => {
        const { activeTranslations } = this.state;
        const newActiveTranslations = { ...activeTranslations };
        delete newActiveTranslations[objectId];
        this.setState({ activeTranslations: newActiveTranslations });
    }

    handleGlobalShortcut = (e) => {
        if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
            e.preventDefault();
            this.searchInputField?.focus();
        }
    }

    componentDidMount() {
        // this.setLoadMore();
        const { holdingStore } = this.props;
        if (holdingStore) {
            this.scrollViewTimer = setInterval(() => holdingStore.postScrollview(), 30 * 1000);
        }
        document.addEventListener('keydown', this.handleGlobalShortcut);
        this.setupVideoObserver();
        this.setupCardObserver();
    }
    componentWillUnmount() {
        // this.unsetLoadMore();
        const { holdingStore } = this.props;
        if (holdingStore) {
            holdingStore.postScrollview()
            clearInterval(this.scrollViewTimer);
        }
        document.removeEventListener('keydown', this.handleGlobalShortcut);
        if (this.videoObserver) {
            this.videoObserver.disconnect();
        }
        if (this.cardObserver) {
            this.cardObserver.disconnect();
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.list !== this.props.list) {
            this.checkHeight();
        }
    }

    componentDidUpdate(prevProps) {
        const { list } = this.props;
        if (list !== prevProps.list) {
            this.checkHeight();
        }
    }

    onClickMenu = e => {
        const { showMenu } = this.props;
        if (showMenu) {
            showMenu(e);
        }
    }

    handleFilterKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.clearSearch();
        }
    }

    handleKeyDown = (e) => {
        const { search, focusedIndex } = this.state;
        const {
            list = [],
            sortBy = [],
            sortTitle = '',
        } = this.props;

        let finalList = list.filter(el => {
            if (!search) {
                return true;
            }
            const searchLower = search.toLowerCase();
            const name = el.name?.toLowerCase() || el.client?.toLowerCase() || '';
            const description = el.description?.toLowerCase() || '';
            const tags = el.tags ? el.tags.join(' ').toLowerCase() : [];
            return name.includes(searchLower) || description.includes(searchLower) || tags.includes(searchLower);
        });
        const listLength = finalList.length;

        if (sortBy.length > 0) {
            finalList = finalList.sort(fieldSorter([sortTitle]));
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = (focusedIndex + 1) % listLength;
            this.setState({
                focusStarted: true,
                focusedIndex: nextIndex,
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevIndex = (focusedIndex - 1 + listLength) % listLength;
            this.setState({
                focusStarted: true,
                focusedIndex: prevIndex,
             });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const obj = finalList[focusedIndex];
            const viewElement = obj.viewElement || this.viewElement;
            viewElement(null, {
                id: obj.id,
            });
        }
    }

    getImageScrollerRef = (ref, id) => {
        const { getImageScrollerRef = () => {} } = this.props;
        // console.log('getImageScrollerRef', ref);
        if (getImageScrollerRef) {
            getImageScrollerRef(ref, id);
        }
    }

    showExternalImage = ({ imageUrl, imageLink, maxImageWidth, maxWidth, maxHeight = '20vh', imageStyles, articleUrl, articleId }) => {
        const { playingVideos } = this.state;
        const isYoutube = this.isYoutubeUrl(articleUrl);
        const youtubeVideoId = isYoutube ? this.getYoutubeVideoId(articleUrl) : null;
        const isPlaying = playingVideos[articleId];

        // If YouTube video is playing, show the embedded player
        if (isYoutube && isPlaying && youtubeVideoId) {
            return (<>
                <div class='w-100' ref={(ref) => this.setVideoRef(articleId, ref)}>
                    <div class='w-100 position-relative' style='padding-bottom: 56.25%;'>
                        <iframe
                            class='position-absolute w-100 h-100'
                            style='top: 0; left: 0; border: none;'
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0&autoplay=1`}
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                            allowFullScreen
                        />
                    </div>
                </div>
            </>);
        }

        return (<>
            <div
                class='d-flex w-100'
                style={`${maxImageWidth ? `width: ${maxImageWidth}; max-width: ${maxImageWidth}; min-width: ${maxImageWidth};` : ''}`}
            >
                <div class='w-100 h-100'>
                    <div class='d-flex flex-row flex-nowrap h-100 w-100'>
                        <div
                            class='w-100 h-100 text-center imageContainer d-flex justify-content-center align-items-center position-relative overflow-hidden'
                            style={`max-height: ${maxHeight}; min-height: 100px;`}
                        >
                            {isYoutube && youtubeVideoId ? <>
                                {/* YouTube thumbnail with play button overlay */}
                                <div
                                    class='w-100 position-relative'
                                    style='cursor: pointer;'
                                    onClick={(e) => this.playVideo(e, articleId, articleUrl)}
                                >
                                    <img
                                        src={`${imageUrl}`}
                                        class='img-fluid w-100'
                                        style={`object-fit: cover; ${imageStyles};`}
                                    />
                                    <div
                                        class='position-absolute d-flex justify-content-center align-items-center'
                                        style='top: 0; left: 0; right: 0; bottom: 0;'
                                    >
                                        <div
                                            class='d-flex justify-content-center align-items-center rounded-circle'
                                            style='width: 68px; height: 68px; background: rgba(255, 0, 0, 0.9);'
                                        >
                                            <i class='fa-solid fa-play text-white' style='font-size: 28px; margin-left: 4px;' />
                                        </div>
                                    </div>
                                </div>
                            </> : imageLink ? <>
                                <a href={imageLink} class='w-100 text-reset link-underline-opacity-0 link-underline' target='_blank' rel='noopener noreferrer'>
                                    <img
                                        src={`${imageUrl}`}
                                        class='img-fluid w-100'
                                        style={`
                                            object-fit: cover;
                                            ${imageStyles};
                                        `}
                                    />
                                </a>
                            </> : articleUrl ? <>
                                <a
                                    href={articleUrl}
                                    class='w-100 text-reset link-underline-opacity-0 link-underline'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={(e) => this.gotoUrl(e, articleId)}
                                >
                                    <img
                                        src={`${imageUrl}`}
                                        class='img-fluid w-100'
                                        style={`
                                            object-fit: cover;
                                            ${imageStyles};
                                        `}
                                    />
                                </a>
                            </> : <>
                                <img
                                    src={`${imageUrl}`}
                                    class='img-fluid w-100'
                                    style={`
                                        object-fit: cover;
                                        ${imageStyles};
                                    `}
                                    onDoubleClick={this.onDoubleClickImage}
                                />
                            </>}
                        </div>
                    </div>
                </div>
            </div>
        </>);
    }

    renderVideoPosterPlaceholder = ({ file }) => {
        const poster = file.s3LinkStreamPreview1;
        return (
            <div class='video-player-wrapper d-flex flex-column w-100'>
                <div
                    class='position-relative w-100 d-flex justify-content-center align-items-center bg-dark'
                    style={`aspect-ratio: 16/9; ${poster ? `background-image: url("${poster}"); background-size: cover; background-position: center;` : ''}`}
                >
                    <span
                        class='d-flex align-items-center justify-content-center rounded-circle'
                        style='width: 56px; height: 56px; background: rgba(0,0,0,0.45); border: 2px solid rgba(255,255,255,0.85);'
                    >
                        <i class='fa-solid fa-play text-white' style='font-size: 1.1rem; margin-left: 3px;' />
                    </span>
                </div>
            </div>
        );
    }

    renderVideoSlide = ({ obj, file, isActive = true, maxHeight = '40vh' }) => {
        const poster = file.s3LinkStreamPreview1;
        if (obj.autoplayVideo) {
            if (!isActive) {
                return this.renderVideoPosterPlaceholder({ file });
            }
            // Sponsor logos (article.sponsors + sponsorsMeta) sit over the bottom
            // edge of the autoplay hero; the overlay renders null when empty.
            if (file.s3LinkStream && componentsOther.AutoplayHlsVideo) {
                return (
                    <div class='position-relative w-100'>
                        <componentsOther.AutoplayHlsVideo poster={poster} hlsUrl={file.s3LinkStream} maxHeight={maxHeight} />
                        {componentsOther.ArticleSponsorOverlay && <componentsOther.ArticleSponsorOverlay articleId={obj.id} surface='frontpage' sponsors={obj.sponsors} sponsorsMeta={obj.sponsorsMeta} />}
                    </div>
                );
            }
            if (file.s3Link && file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
                return (
                    <div class='position-relative w-100'>
                        <div class='video-player-wrapper d-flex flex-column w-100'>
                            <video autoplay muted loop playsinline poster={poster} class='w-100' style={`object-fit: cover; max-height: ${maxHeight};`} src={file.s3Link} />
                        </div>
                        {componentsOther.ArticleSponsorOverlay && <componentsOther.ArticleSponsorOverlay articleId={obj.id} surface='frontpage' sponsors={obj.sponsors} sponsorsMeta={obj.sponsorsMeta} />}
                    </div>
                );
            }
            return null;
        }
        // Portrait clips open in the full-screen swipe player instead of playing
        // inline — a 9:16 video inside the narrow feed column wastes most of the
        // screen. Matches the equivalent branch in article/view.js renderVideoFile.
        // Members-only posts are excluded: get-shorts.js never lists them, so
        // routing there would deep-link into a player that can't find this clip
        // and silently pins to a different one. Fall through to the normal inline
        // player below instead.
        if (file.videoOrientation === 'portrait' && file.s3LinkStream && !obj.requiresMembership) {
            return (
                <div key={file.s3LinkStream} class='video-player-wrapper d-flex flex-column w-100'>
                    <figure class='mb-0'>
                        <div
                            class='position-relative rounded-3 overflow-hidden mx-auto'
                            style={`max-width: 340px; aspect-ratio: 9/16; max-height: 70vh; cursor: pointer; background-image: url("${poster}"); background-size: cover; background-position: center;`}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                route(`/feed/shorts/${obj.uuidv4}`);
                            }}
                        >
                            <span
                                class='position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle'
                                style='width: 56px; height: 56px; background: rgba(0,0,0,0.45); border: 2px solid rgba(255,255,255,0.85);'
                            >
                                <i class='fa-solid fa-play text-white' style='font-size: 1.1rem; margin-left: 3px;' />
                            </span>
                        </div>
                    </figure>
                </div>
            );
        }
        if (!isActive) {
            // Off-screen carousel slides never mount a player; they upgrade to the
            // real rendering when scrolled into view.
            return this.renderVideoPosterPlaceholder({ file });
        }
        if (file.s3LinkStream && componentsOther.VideoStreamingPlayer) {
            return (
                <div class='video-player-wrapper d-flex flex-column w-100'>
                    <componentsOther.VideoStreamingPlayer
                        key={file.s3LinkStream}
                        videoStreamUrl={file.s3LinkStream}
                        stores={this.props.stores}
                        poster={poster}
                        id={obj.id}
                        onDoubleClick={this.onDoubleClickImage}
                        transcript={file.transcript}
                        transcriptionSegments={file.transcriptionSegments}
                        transcriptionLanguage={file.transcriptionLanguage}
                        transcriptEnglish={file.transcriptEnglish}
                        transcriptionSegmentsEnglish={file.transcriptionSegmentsEnglish}
                        transcriptSpanish={file.transcriptSpanish}
                        transcriptionSegmentsSpanish={file.transcriptionSegmentsSpanish}
                        transcriptFrench={file.transcriptFrench}
                        transcriptionSegmentsFrench={file.transcriptionSegmentsFrench}
                        transcriptFinnish={file.transcriptFinnish}
                        transcriptionSegmentsFinnish={file.transcriptionSegmentsFinnish}
                    />
                </div>
            );
        }
        if (file.s3Link && file.s3Link.match(/\.(m4v|m4a|mp4)$/i)) {
            return (
                <div class='video-player-wrapper d-flex flex-column w-100'>
                    <video controls class='w-100'>
                        <source src={file.s3Link} type='video/mp4' />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }
        if (file.s3Link && file.s3Link.match(/\.(mov)$/i)) {
            return (
                <div class='video-player-wrapper d-flex flex-column w-100'>
                    <video controls class='w-100'>
                        <source src={file.s3Link} type='video/quicktime' />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }
        return null;
    }

    renderImageOverlay(imageOverlay, imageOverlayLocation) {
        const overlayHelpers = componentsOther.backgroundPostText;
        if (!overlayHelpers) {
            return null;
        }
        const overlayKey = overlayHelpers.imageOverlayKey(imageOverlay);
        if (overlayKey === 'none') {
            return null;
        }
        if (overlayKey === 'location-sticker') {
            const label = typeof imageOverlayLocation === 'string' ? imageOverlayLocation.trim().slice(0, 80) : '';
            return (
                <div class='article-image-overlay article-image-overlay-location-sticker'>
                    <Localizer>
                        <div
                            class='article-image-location-sticker'
                            aria-label={label
                                ? <Text id='article.imageOverlayLocationAria' fields={{ label }}>Location: {label}</Text>
                                : <Text id='article.imageOverlayLocationFallback'>Location</Text>}
                        >
                            <i class='fa-solid fa-location-dot' aria-hidden='true' />
                            <span>{label || <Text id='article.imageOverlayLocationFallback'>Location</Text>}</span>
                        </div>
                    </Localizer>
                </div>
            );
        }
        const particles = overlayHelpers.imageOverlayParticles(overlayKey);
        return (
            <div class={`article-image-overlay article-image-overlay-${overlayKey}`} aria-hidden='true'>
                {particles.map((particle, idx) => (
                    <span
                        class='article-image-overlay-particle'
                        key={`feed-image-overlay-${idx}`}
                        style={particle.style}
                    >
                        {particle.symbol}
                    </span>
                ))}
            </div>
        );
    }

    renderImageSticker(imageStickerText, imageStickerPosition) {
        const helpers = componentsOther.backgroundPostText;
        if (!helpers) {
            return null;
        }
        const text = helpers.cleanImageStickerText(imageStickerText);
        if (!text) {
            return null;
        }
        const position = helpers.imageStickerPositionKey(imageStickerPosition);
        return (
            <div class={`article-image-sticker article-image-sticker-${position}`}>
                <span class='article-image-sticker-text'>{text}</span>
            </div>
        );
    }

    renderStoryText(obj) {
        const bgText = componentsOther.backgroundPostText;
        const { elOverlayText } = this.props;
        if (!bgText || !elOverlayText || !bgText.isStoryPost(obj)) {
            return null;
        }
        const articleBg = componentsOther.articleBackgroundStyles
            ? componentsOther.articleBackgroundStyles({ background: obj.background, backgroundImage: obj.backgroundImage })
            : {};
        const perLine = bgText.usesPerLineSizes(obj.ingress);
        const styleKey = bgText.backgroundTextStyleKey(obj.backgroundTextStyle);
        const decoration = bgText.backgroundDecorationKey(obj.backgroundDecoration);
        const symbols = bgText.backgroundDecorationSymbols(decoration);
        const fontFamily = bgText.backgroundFontCss(obj.backgroundFont);
        const classes = ['story-text-layer background-post-display', ...bgText.backgroundTextClasses(obj)].join(' ');
        return (
            <div
                class={classes}
                style={`
                    ${fontFamily ? `font-family: ${fontFamily};` : ''}
                    color: ${bgText.backgroundTextStyleColor(styleKey, articleBg.textColor || '#ffffff')};
                    --background-text-outline: ${bgText.backgroundTextStyleOutlineColor(styleKey, articleBg.textOutlineColor || 'rgba(0,0,0,0.72)')};
                    text-align: ${obj.backgroundAlign === 'left' ? 'left' : 'center'};
                `}
            >
                <div class='story-text-scrim' aria-hidden='true' />
                {symbols.length > 0 && <div class={`background-decoration-layer background-decoration-${decoration}`} aria-hidden='true'>
                    {symbols.map((symbol, idx) => <span class='background-decoration-item' key={`story-decoration-${idx}`}>{symbol}</span>)}
                </div>}
                <div
                    class='story-text-content box-line-content'
                    style={`font-size: ${perLine ? '1em' : bgText.uniformBackgroundFontSize(obj.ingress)}; line-height: ${perLine ? '1.15' : '1.0em'};`}
                >
                    {elOverlayText(obj)}
                </div>
            </div>
        );
    }

    showImage({ obj, maxImageWidth, maxWidth, maxHeight = '40vh', imageContainerStyles, imageStyles, hasVideo, imageLocation, currentListContainerClassname, youtubeLink }) {
        const { elLink, showEmptyImage } = this.props;
        const hasImages = obj.images && obj.images.length > 0;
        const imageFilter = componentsOther.backgroundPostText
            ? componentsOther.backgroundPostText.imageFilterKey(obj.imageFilter)
            : 'none';
        const imageFilterClass = `article-image-filter-target article-image-filter-${imageFilter}`;
        if (imageLocation === 'right' || imageLocation === 'left' || currentListContainerClassname !== 'w-100') {
            return (<>
                {obj.images[0] ? <>
                    <div
                        class='d-flex'
                        style={`${maxImageWidth ? `width: ${maxImageWidth}; max-width: ${maxImageWidth}; min-width: ${maxImageWidth};` : ''}`}
                    >
                        <div class='w-100 h-100'>
                            <div class='d-flex flex-row flex-nowrap h-100'>
                                <div
                                    class='w-100 h-100 text-center imageContainer d-flex justify-content-center align-items-center position-relative overflow-hidden'
                                    style={`max-height: ${maxHeight}; min-height: 100px;`}
                                >
                                    {elLink ? <>
                                        <a href={elLink(obj)} class='text-reset link-underline-opacity-0 link-underline' target='_blank' rel='noopener noreferrer'>
                                            <img
                                                src={`${obj.images[0].s3LargeLink}`}
                                                class={`img-fluid ${imageFilterClass}`}
                                                style={`
                                                    object-fit: cover;
                                                    ${imageStyles};
                                                `}
                                            />
                                        </a>
                                    </> : <>
                                        <img
                                            src={`${obj.images[0].s3LargeLink}`}
                                            class={`img-fluid ${imageFilterClass}`}
                                            style={`
                                                object-fit: cover;
                                                ${imageStyles};
                                            `}
                                            onDoubleClick={this.onDoubleClickImage}
                                        />
                                    </>}
                                    {this.renderImageOverlay(obj.imageOverlay, obj.imageOverlayLocation)}
                                    {this.renderImageSticker(obj.imageStickerText, obj.imageStickerPosition)}
                                    {this.renderStoryText(obj)}
                                    {obj.saleType && <>
                                        <div class='position-absolute start-0 bottom-0 w-100 px-2 py-1' style='background: rgba(220, 53, 69, 0.9);'>
                                            <span class='text-white fw-bold' style='font-size: 0.9em;'>{obj.saleType}</span>
                                        </div>
                                    </>}
                                </div>
                            </div>
                        </div>
                    </div>
                </> : <></>}
            </>)
        }

        return (<>
            <div class='d-flex' style={`${(hasVideo || youtubeLink) ? 'width: 100%;' : (maxImageWidth ? `width: ${maxImageWidth}; max-width: ${maxImageWidth}; min-width: ${maxImageWidth};` : '')}`}>
                {hasVideo ? (() => {
                    const videoFiles = (obj.files || []).filter(file => file.s3LinkStream
                        || (file.s3Link && file.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i)));
                    if (videoFiles.length >= 2) {
                        return (
                            <VideoScroller
                                files={videoFiles}
                                stores={this.props.stores}
                                id={obj.id}
                                renderSlide={(file, isActive) => this.renderVideoSlide({ obj, file, isActive, maxHeight })}
                            />
                        );
                    }
                    if (obj.autoplayVideo && obj.files?.length > 0) {
                        return this.renderVideoSlide({ obj, file: obj.files[0], isActive: true, maxHeight });
                    }
                    if (obj.files?.length > 0) {
                        return obj.files.map(file => this.renderVideoSlide({ obj, file, isActive: true, maxHeight }));
                    }
                    return null;
                })() : <>
                    {hasImages ? <>
                        <div class='position-relative w-100'>
                            <ImageScroller
                                images={obj.images}
                                stores={this.props.stores}
                                showImg={img => localUtil.displayImg(img, this.props, '1024x')}
                                showImageInfo={false}
                                onDoubleClick={this.onDoubleClickImage}
                                maxHeight={maxHeight}
                                maxWidth={maxWidth}
                                rounded={false}
                                imageContainerStyles={imageContainerStyles}
                                imageStyles={imageStyles}
                                classNames={`h-100 article-image-filter-scope article-image-filter-${imageFilter}`}
                                classNamesWrapper1='h-100'
                                id={obj.id}
                                getRef={this.getImageScrollerRef}
                            />
                            {this.renderImageOverlay(obj.imageOverlay, obj.imageOverlayLocation)}
                            {this.renderImageSticker(obj.imageStickerText, obj.imageStickerPosition)}
                            {this.renderStoryText(obj)}
                            {componentsOther.ArticleSponsorOverlay && <componentsOther.ArticleSponsorOverlay articleId={obj.id} surface='frontpage' sponsors={obj.sponsors} sponsorsMeta={obj.sponsorsMeta} />}
                            {obj.saleType && <>
                                <div class='position-absolute start-0 bottom-0 w-100 px-2 py-1' style='background: rgba(220, 53, 69, 0.9);'>
                                    <span class='text-white fw-bold' style='font-size: 0.9em;'>{obj.saleType}</span>
                                </div>
                            </>}
                        </div>
                    </> : <>
                        {youtubeLink ? <>
                            <div class='w-100'>
                                {localUtil.youtubeVideo(youtubeLink)}
                            </div>
                        </> : <></>}
                    </>}
                </>}
            </div>
        </>);
    }

    checkHeight = () => {
        const { checkContentHeight = false, list = [] } = this.props;
        const { extended = {}, noToggle = {} } = this.state;
        if (!checkContentHeight) {
            return;
        }
        if (this.contentContainer) {
            const keys = Object.keys(this.contentContainer);
            const checkExtended = {};
            const checkNoToggle = {};

            keys.forEach(key => {
                const height = this.contentContainer[key] ? this.contentContainer[key].scrollHeight : 0;
                // Check if this article has a poll - if so, always show full content
                const article = list.find(a => String(a.id) === key);
                const hasPoll = article?.polls?.length > 0;

                if (hasPoll) {
                    // Articles with polls should always be fully visible
                    checkNoToggle[key] = true;
                } else if (height > MAX_HEIGHT) {
                    checkExtended[key] = extended[key] || false;
                } else {
                    checkNoToggle[key] = noToggle[key] || true;
                }
            });
            this.setState({ extended: checkExtended, noToggle: checkNoToggle });
        }
    }

    toggleExtended = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { extended = {} } = this.state;
        extended[id] = !extended[id];
        this.setState({ extended });
    }

    gotoUrl = (e, articleId) => {
        e.stopPropagation();
        e.preventDefault();
        const url = e.target.closest('a').href
        if (url) {
            const { appState } = this.props.stores;
            appState.addUsageLog({
                type: 'feed_link_click',
                props: {
                    articleId,
                    url,
                },
                timestamp: Math.floor(new Date().getTime() / 1000),
            });
            window.open(url, '_blank');
        }
    }

    renderContent = ({ obj, idx }) => {
        const {
            title = '',
            list = [],
            classNames = '',
            qty,
            elHeader,
            elTitle = e => e.title,
            elTitleRight = e => e.elTitleRight,
            elLink,
            elImageStatus = e => '',
            elDescription = e => '',
            elOverlayText = () => null,
            elInfo1 = e => '',
            elInfo2 = e => '',
            elInfo3 = e => '',
            viewTags,
            inc,
            dec,
            remove,
            editQty,
            wrapperProps = {},
            deleteElement,
            rentOutElement,
            moveElement,
            checkElement,
            addUsageData,
            editElement,
            editFunction,
            refreshElement,
            showCheckElement = () => true,
            showIncDec = () => true,
            showEmptyImage = true,
            showCevron = false,
            showRightIcon,
            rightBoxField = '',
            rightBoxFieldValue = e => e,
            rightBoxFieldPostfix = '',
            rightBoxFieldPostfixField,
            rightBoxFieldPostfixValue,
            rightBoxFieldBottomValue,

            rightBoxTitle,
            rightBoxValue = () => '',
            rightBoxClass = () => '',
            maxHeight = '25vh',
            maxWidth,
            imageContainerStyles = '',
            imageStyles, //  = 'aspect-ratio: 4/3;',

            listClasses = [],
            listStyles = [],
            listTitleStyles = [],
            listContainerClassnames = [],
            listWrapperClassnames = [],
            listMaxImageWidth = [],
            listImageLocations = [],

            showBylineHeader,
            showComments,
            showLikes,
            likeCommentType,

            elBylineHeader,

            imgHeight,
            showMenu,
            showQty = true,
            holdingStore,
            checkContentHeight = false,
            showFiles = false,
            showTranslation = false,
            elAboveLikes,
            elAction,
        } = this.props;
        const { appState, userStore } = this.props.stores;
        const { opts = {} } = appState;

        // Translation state for this object
        const { activeTranslations = {}, translationLoading = {} } = this.state;
        const activeTranslation = activeTranslations[obj.id];
        const isTranslationLoading = !!translationLoading[obj.id];
        const loadingLanguage = translationLoading[obj.id];

        // Determine object type for translation API
        const translationObjectType = likeCommentType === 'news' ? 'news' : 'article';

        const { extended, noToggle, localLikes, showHeart } = this.state;
        // console.log('renderContent', { localLikes });

        const { absDiffX, swipeId, swipeDirection, refreshElementInProgress, focusedIndex, focusStarted } = this.state;
        const swipeMenuIsOpen = absDiffX > 0;

        const hasSwipeLeftFunctions = (deleteElement || rentOutElement || moveElement || checkElement || addUsageData);
        const hasSwipeRightFunctions = (editElement);
        const hasSwipeFunctions = (hasSwipeLeftFunctions || hasSwipeRightFunctions);

        let isElementSwipedOpen = false;
        if (swipeDirection === 'left' && opts.swipeLeft) {
            isElementSwipedOpen = (
                swipeId === obj.id &&
                swipeMenuIsOpen &&
                hasSwipeLeftFunctions
            );
        } else if (swipeDirection === 'right' && opts.swipeRight) {
            isElementSwipedOpen = (
                swipeId === obj.id &&
                swipeMenuIsOpen &&
                hasSwipeRightFunctions
            );
        }

        const { user } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const hasVideo = Array.isArray(obj?.files) ? obj.files.some(file => file.s3LinkStream || file.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i)) : false;
        const hasOtherFilesThanVideo = Array.isArray(obj?.files) ? obj.files.some(file => file.s3Link && !file.s3Link.match(/\.(m4v|m4a|mp4|mov)$/i)) : false;

        const hasImage = obj.images?.length > 0;
        const hasImageOrVideo = hasImage || hasVideo;
        const hasUrl = !!obj.url && !!obj.urlTitle;
        const articleBg = componentsOther.articleBackgroundStyles
            ? componentsOther.articleBackgroundStyles({ background: obj.background, backgroundImage: obj.backgroundImage })
            : {};

        // Background posts hold only a short composer ingress (the composer drops the
        // background when the weighted length exceeds the budget), so they always show
        // their full text: no height clamp, no show more/less toggle, and the wrapper
        // below has no max-height. Gate + sizing live in lib/backgroundPostText.js
        // (via the registry); the inline expressions are the stubbed-registry fallback.
        const bgText = componentsOther.backgroundPostText;
        const showBackground = bgText
            ? bgText.isBackgroundPost(obj)
            : (!hasImageOrVideo && (obj.background || obj.backgroundImage) && obj.ingress?.length <= 150);
        // Per-line mode: the sized line divs (rendered by elDescription) carry their
        // own em font sizes, so the wrapper stays at 1em with a unitless line-height.
        const perLineSizes = showBackground && bgText && bgText.usesPerLineSizes(obj.ingress);
        const fontSize = perLineSizes ? '1em' : (bgText
            ? bgText.uniformBackgroundFontSize(obj.ingress)
            : (obj.ingress?.length < 30 ? '3.5em' : '2.5em'));
        const backgroundFontFamily = showBackground && bgText ? bgText.backgroundFontCss(obj.backgroundFont) : '';
        const backgroundTextStyle = showBackground && bgText
            ? bgText.backgroundTextStyleKey(obj.backgroundTextStyle)
            : 'auto';
        const backgroundDecoration = showBackground && bgText
            ? bgText.backgroundDecorationKey(obj.backgroundDecoration)
            : 'none';
        const decorationSymbols = showBackground && bgText
            ? bgText.backgroundDecorationSymbols(backgroundDecoration)
            : [];
        const backgroundMotion = showBackground && bgText
            ? bgText.backgroundMotionKey(obj.backgroundMotion)
            : 'none';
        const backgroundMotionParticles = showBackground && bgText
            ? bgText.backgroundMotionParticleStyles(backgroundMotion)
            : [];
        const backgroundTextColor = showBackground && bgText
            ? bgText.backgroundTextStyleColor(backgroundTextStyle, articleBg.textColor)
            : articleBg.textColor;
        const backgroundTextOutline = showBackground && bgText
            ? bgText.backgroundTextStyleOutlineColor(backgroundTextStyle, articleBg.textOutlineColor)
            : articleBg.textOutlineColor;
        const backgroundDisplayClass = showBackground
            ? ['background-post-display', ...(bgText ? bgText.backgroundTextClasses(obj) : [])].join(' ')
            : '';
        const backgroundContainerClass = [
            'w-100 d-flex flex-column justify-content-center align-items-center',
            'ps-3 pe-2 position-relative',
            backgroundDisplayClass,
        ].filter(Boolean).join(' ');

        const currentListContainerClassname = listContainerClassnames[idx] || 'w-100';
        const currentListWrapperClassname = listWrapperClassnames[idx] || '';
        const currentListClasses = listClasses[idx] || 'flex-column justify-content-between';
        const currentListStyles = listStyles[idx] || '';
        const currentListTitleStyles = listTitleStyles[idx] || '';
        const maxImageWidth = listMaxImageWidth[idx] || '';
        const imageLocation = listImageLocations[idx] || 'top';

        // Check if obj.ingress or obj.body has a youtube link and if so, retrieve the link to the video.
        // Note: YouTube links in obj.body are handled by replaceInlineContent in elDescription rendering.
        let youtubeLink;
        const youtubeRegex = /https?:\/\/(www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/;
        const youtubeShortRegex = /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/;
        const youtubeShortsRegex = /https?:\/\/(www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/;
        if (obj.ingress && obj.ingress.match(youtubeRegex)) {
            const youtubeId = obj.ingress.match(youtubeRegex)[2];
            youtubeLink = `https://www.youtube.com/watch?v=${youtubeId}`;
        }
        if (!youtubeLink && obj.body && obj.body.match(youtubeRegex)) {
            const youtubeId = obj.body.match(youtubeRegex)[2];
            youtubeLink = `https://www.youtube.com/watch?v=${youtubeId}`;
        }
        if (!youtubeLink && obj.url) {
            const urlMatch = obj.url.match(youtubeRegex) || obj.url.match(youtubeShortRegex) || obj.url.match(youtubeShortsRegex);
            if (urlMatch) {
                const youtubeId = urlMatch[2] || urlMatch[1];
                youtubeLink = `https://www.youtube.com/watch?v=${youtubeId}`;
            }
        }
        if (!youtubeLink && obj.youtube) {
            const ytMatch = obj.youtube.match(youtubeRegex) || obj.youtube.match(youtubeShortRegex) || obj.youtube.match(youtubeShortsRegex);
            if (ytMatch) {
                const youtubeId = ytMatch[2] || ytMatch[1];
                youtubeLink = `https://www.youtube.com/watch?v=${youtubeId}`;
            }
        }
        // Check if body contains a YouTube URL (for max-height override, not for rendering)
        const bodyHasYoutube = obj.body && (obj.body.match(/youtube\.com/) || obj.body.match(/youtu\.be/));

        return (<>
            <div class={`w-100 d-flex flex-row position-relative mb-4 pb-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${currentListWrapperClassname} overflow-hidden`} style={`border: 0.5px solid ${darkmode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'};`} ref={ref => this.setCardRef(obj.id, ref)}>
                <div
                    class={`w-100 flex-fill d-flex ${currentListClasses} box-line overflow-hidden position-relative ${obj.classNames || ''} ${(focusStarted && focusedIndex === idx) ? 'box-line-focused' : ''}`}
                    onClick={obj.viewElement || this.viewElement}
                    data-isopen={swipeMenuIsOpen}
                    data-id={obj.id}
                    data-title={elTitle(obj)}
                    data-type={holdingStore?.name}
                    {...wrapperProps}
                    onTouchstart={hasSwipeFunctions && this.touchStart}
                    onTouchend={hasSwipeFunctions && this.touchEnd}
                    onTouchmove={hasSwipeFunctions && this.touchMove}
                    style={`
                        ${obj.styles || ''}
                        ${isElementSwipedOpen ? `
                            overflow: hidden;
                            left: ${swipeDirection === 'left' ? `-` : ''}${absDiffX || 0}px !important;
                        ` : ''}
                        ${currentListStyles}
                    `}
                >
                    {this.props.elPendingBadge && this.props.elPendingBadge(obj) && <div class='d-flex flex-row w-100'>
                        {this.props.elPendingBadge(obj)}
                    </div>}
                    {showBylineHeader && <>
                        <div class='d-flex flex-row justify-content-between w-100'>
                            {elBylineHeader(obj)}
                        </div>
                    </>}
                    {(imageLocation === 'top') && <>
                        {elHeader && <div class='d-flex flex-row w-100'>
                            {elHeader(obj)}
                        </div>}
                    </>}
                    {(imageLocation === 'left' || imageLocation === 'top') && <>
                        {this.showImage({ obj, maxImageWidth, maxWidth, maxHeight, imageContainerStyles, imageStyles, hasVideo, imageLocation, currentListContainerClassname, youtubeLink })}
                    </>}

                    {editFunction &&  obj.user === user.id && <>
                        <div class='position-absolute' style='top: 10px; right: 10px;'>
                            <button
                                type='button'
                                class='btn btn-link py-0'
                                onClick={editFunction}
                                data-id={obj.id}
                            >
                                <i class='fa-solid fa-pencil text-secondary' />
                            </button>
                        </div>
                    </>}


                    {/* swipeIdOpen: {swipeIdOpen} */}
                    {/* swipeId: {swipeId} */}
                    {/* diffX: {diffX} */}
                    {/* isSwipedOpen: {JSON.stringify(isSwipedOpen, null, 4)} */}
                    {/* isSwipedOpen: {JSON.stringify({ diffX, isSwipedOpen, swipeId}, null, 4)} */}
                    {showQty ? <>
                        {qty ? <>
                            <div class='box-line-qty text-end'>{qty(obj)}</div>
                        </> : <>
                            {util.isDefined(obj.qty) && <div
                                class='box-line-qty text-end'
                                onClick={opts.allowEditQty ? editQty : () => {}}
                                data-id={obj.id}
                                data-qty={obj.qty}
                            >
                                {obj.qty}
                            </div>}
                        </>}
                    </> : <></>}
                    {obj.icon && <>
                        <div class='box-line-icon mt-2 ms-3' style='font-size: 1.7em; width: 25px;'>
                            <i class={obj.icon} />
                        </div>
                    </>}
                    <div class={`${hasUrl ? '' : 'flex-fill'} d-flex flex-column ${showBackground || hasUrl ? '' : 'pt-2'} overflow-hidden`}>
                        {(imageLocation !== 'top') && <>
                            {elHeader && <div class='d-flex flex-row w-100'>
                                {elHeader(obj)}
                            </div>}
                        </>}
                        {/* Hide content section when there's a URL preview - show video directly */}
                        {!hasUrl && <>
                            <div
                                class={backgroundContainerClass}
                                style={`${showBackground ? `
                                    ${articleBg.backgroundImageCss ? `background-image: ${articleBg.backgroundImageCss};` : ''}
                                    ${articleBg.backgroundColor ? `background-color: ${articleBg.backgroundColor};` : ''}
                                    ${backgroundFontFamily ? `font-family: ${backgroundFontFamily};` : ''}
                                    color: ${backgroundTextColor || 'inherit'};
                                    --background-text-outline: ${backgroundTextOutline || 'rgba(0,0,0,0.72)'};
                                    font-size: ${fontSize || '1.2em'};
                                    font-weight: 500 !important;
                                    line-height: ${perLineSizes ? '1.15' : '1.0em'};
                                    text-align: ${obj.backgroundAlign === 'left' ? 'left' : 'center'};
                                    padding: 1em;
                                    min-height: 30vh;
                                ` : ''}`}
                            >
                                {backgroundMotion !== 'none' && <div
                                    class={`background-motion-layer background-motion-${backgroundMotion}`}
                                    aria-hidden='true'
                                >
                                    {backgroundMotionParticles.map((particleStyle, particleIdx) => (
                                        <span
                                            class='background-motion-particle'
                                            key={`feed-motion-particle-${particleIdx}`}
                                            style={particleStyle}
                                        />
                                    ))}
                                </div>}
                                {decorationSymbols.length > 0 && <div
                                    class={`background-decoration-layer background-decoration-${backgroundDecoration}`}
                                    aria-hidden='true'
                                >
                                    {decorationSymbols.map((symbol, decorationIdx) => (
                                        <span
                                            class='background-decoration-item'
                                            key={`feed-decoration-${decorationIdx}`}
                                        >
                                            {symbol}
                                        </span>
                                    ))}
                                </div>}
                                {/* <GradientRight stores={this.props.stores} /> */}
                                <div
                                    class='box-line-content w-100 overflow-hidden'
                                    style={`
                                        max-height: ${!checkContentHeight || showBackground || extended[obj.id] || obj.polls?.length > 0 || youtubeLink ? 'unset' : (bodyHasYoutube ? '70vh' : `${MAX_HEIGHT}px;`)};
                                    `}
                                    ref={el => this.contentContainer[obj.id] = el}
                                >
                                    <div
                                        class='box-line-title px-0 overflow-hidden position-relative w-100 d-flex flex-row justify-content-between align-items-center'
                                        style={`
                                            white-space: normal !important;
                                            ${currentListTitleStyles}
                                            text-overflow: ellipsis;
                                        `}
                                    >
                                        {elLink ? <>
                                            <a href={elLink(obj)} class='text-reset link-underline-opacity-0 link-underline'>
                                                {activeTranslation?.translation?.title ? (
                                                    <span class='mb-0 mt-2 py-0'>{activeTranslation.translation.title}</span>
                                                ) : elTitle(obj)}
                                            </a>
                                        </> : <>
                                            {activeTranslation?.translation?.title ? (
                                                <span class='mb-0 mt-2 py-0'>{activeTranslation.translation.title}</span>
                                            ) : elTitle(obj)}
                                        </>}
                                        {elTitleRight ? <>
                                            <div class='d-flex'>
                                                {elTitleRight(obj)}
                                            </div>
                                        </> : <></>}
                                    </div>
                                    <div class={`box-line-body px-0 ${!hasImageOrVideo && !hasUrl && !showBackground ? 'drop-cap-text' : ''}`}>
                                        {activeTranslation?.translation ? <>
                                            {activeTranslation.translation.ingress && <div class='mt-2 feed-post-text'><Markdown markdown={activeTranslation.translation.ingress} /></div>}
                                            {activeTranslation.translation.body && <div class='font-weight-normal w-100 mt-3 mb-2 feed-post-text'><Markdown markdown={activeTranslation.translation.body} /></div>}
                                        </> : <>
                                            {elDescription(obj) && <div>
                                                {elDescription(obj)}
                                            </div>}
                                        </>}
                                    </div>

                                    {/* Read more button for shared news articles */}
                                    {obj.sourceRaceId > 0 && obj.sourceUrl && <>
                                        <div class='d-flex flex-row justify-content-start mt-2'>
                                            <button
                                                class='btn btn-sm btn-outline-primary rounded-pill'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    route(obj.sourceUrl.replace(/^#/, ''));
                                                }}
                                            >
                                                <i class='fa-solid fa-arrow-right me-1' />
                                                <Text id='article.read-more'>Read more</Text>
                                            </button>
                                        </div>
                                    </>}

                                </div>
                                {/* checkContentHeight: { checkContentHeight ? 'true' : 'false' }<br /> */}
                                {/* extended: {JSON.stringify(extended[obj.id], null, 4)}<br /> */}
                                {/* noToggle: {JSON.stringify(noToggle[obj.id], null, 4)}<br /> */}

                                {(!checkContentHeight || showBackground || noToggle[obj.id]) ? <></> : <>
                                    <div class='w-100'>
                                        {extended[obj.id] ? <>
                                            <button
                                                class='btn btn-link btn-sm text-muted w-100 py-2'
                                                data-id={obj.id}
                                                onClick={this.toggleExtended}
                                            >
                                                <i class='fa-duotone fa-chevron-up me-2' /> <Text id='gui.viewbox-less'>Show less</Text>
                                            </button>
                                        </> : <>
                                            <button
                                                class='btn btn-link btn-sm text-muted w-100 py-2'
                                                data-id={obj.id}
                                                onClick={this.toggleExtended}
                                            >
                                                <i class='fa-duotone fa-chevron-down me-2' /> <Text id='gui.viewbox-more'>Show more</Text>
                                            </button>
                                        </>}
                                    </div>
                                </>}
                                {showMenu && <div
                                    class='position-absolute'
                                    style='top: 10px; right: 10px; z-index: 100;'
                                >
                                    <button
                                        type='button'
                                        class='btn btn-lg btn-link py-0 list-menu'
                                        onClick={this.onClickMenu}
                                        data-id={obj.id}
                                    >
                                        <i class='fa-solid fa-ellipsis-vertical text-secondary' />
                                    </button>
                                </div>}
                            </div>
                        </>}
                        {hasOtherFilesThanVideo && showFiles && obj.files && obj.files.length > 0 && <>
                            <div class={`d-flex flex-row justify-content-center py-1 bg-light`}>
                                <small>
                                    {obj.files.map(file => {
                                        return (<>
                                            {/* <xmp>{JSON.stringify(file, null, 4)}</xmp> */}
                                            <a
                                                href={file.s3Link}
                                                onClick={(e) => e.stopPropagation()}
                                                onAuxClick={(e) => e.stopPropagation()}  // middle-click
                                                class='mx-2 text-reset link-underline-opacity-0 link-underline text-truncate'
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                <i class="fa-solid fa-file-pdf"></i> {localUtil.makeShortFileName(file.name, 10)} <span class='font-weight-lighter'>({localUtil.formatFileSize(file.size)})</span>
                                            </a>
                                        </>);
                                    })}
                                </small>
                            </div>
                        </>}
                        {!hasUrl && <>
                            <div class='box-line-info d-flex flex-row justify-content-between px-3 pb-0 position-relative'>
                                {/* <GradientRight stores={this.props.stores} /> */}
                                <div class='box-line-info-item'>
                                    {elInfo1(obj)}
                                </div>
                                <div class='box-line-info-item'>
                                    {elInfo2(obj)}
                                </div>
                                <div class='box-line-info-item'>
                                    {elInfo3(obj)}
                                </div>
                            </div>
                            <div class={`box-line-info d-flex flex-row justify-content-between px-3 pb-0 ${showBackground ? '' : 'mb-2'} position-relative`}>
                                {/* <GradientRight stores={this.props.stores} /> */}
                                {viewTags && <>
                                    {viewTags(obj)}
                                </>}
                            </div>
                            {/* Likes for non-URL posts */}
                            {elAboveLikes && elAboveLikes(obj)}
                            {!showBackground && showLikes && <>
                                <Likes
                                    stores={this.props.stores}
                                    type={obj._likeType || likeCommentType || 'article'}
                                    objectId={obj.id}
                                    uuidv4={obj.uuidv4}
                                    likes={obj.likes}
                                    inputLikes={localLikes}
                                    shareTitle={obj.title}
                                    shareSummary={obj.ingress || obj.body}
                                    shareImage={obj.images?.[0]?.s3SmallLink}
                                    showComment={false}
                                >
                                    {elAction && elAction(obj)}
                                    {showTranslation && <>
                                        <TranslationButtons
                                            collapsible={true}
                                            objectId={obj.id}
                                            objectType={translationObjectType}
                                            translations={obj.translations}
                                            activeLanguage={activeTranslation?.language}
                                            onTranslate={(language) => this.handleTranslate(obj.id, language, translationObjectType)}
                                            onClearTranslation={() => this.clearTranslation(obj.id)}
                                            loading={isTranslationLoading}
                                            loadingLanguage={loadingLanguage}
                                        />
                                    </>}
                                </Likes>
                            </>}
                        </>}
                    </div>

                    {obj.urlImageUrl && !this.isYoutubeUrl(obj.url) && <>
                        {this.showExternalImage({ imageUrl: obj.urlImageUrl, imageLink: obj.urlImageLink, maxImageWidth, maxWidth, maxHeight: '40vh', imageStyles, articleUrl: obj.url, articleId: obj.id })}
                    </>}

                    {obj.urlTitle && !this.isYoutubeUrl(obj.url) && <>
                        <div class='d-flex flex-column bg-light p-2'>
                            <a href={obj.url} class='text-reset text-decoration-none' target='_blank' rel='noopener noreferrer' onClick={(e) => this.gotoUrl(e, obj.id)}>
                                <div class='d-flex flex-row w-100'>
                                    <small class='text-muted text-truncate'>
                                        {obj.urlDomain}
                                    </small>
                                </div>
                                <div class='d-flex flex-row w-100'>
                                    <span class='text-truncate font-weight-bolder'>
                                        {obj.urlTitle}
                                    </span>
                                </div>
                                <div class='d-flex flex-row w-100'>
                                    <small class='text-truncate font-weight-light'>
                                        {obj.urlDescription}
                                    </small>
                                </div>
                            </a>
                        </div>
                    </>}

                    {/* Show description/ingress below the URL section when there's a URL preview */}
                    {hasUrl && elDescription(obj) && <>
                        <div class='px-3 py-2'>
                            {elDescription(obj)}
                        </div>
                    </>}

                    {(showBackground || hasUrl) && <>
                        {elAboveLikes && elAboveLikes(obj)}
                        {showLikes && <>
                            <Likes
                                stores={this.props.stores}
                                type={obj._likeType || likeCommentType || 'article'}
                                objectId={obj.id}
                                uuidv4={obj.uuidv4}
                                likes={obj.likes}
                                inputLikes={localLikes}
                                shareTitle={obj.title}
                                shareSummary={obj.ingress || obj.body}
                                shareImage={obj.images?.[0]?.s3SmallLink}
                                showComment={false}
                            >
                                {elAction && elAction(obj)}
                                {showTranslation && <>
                                    <TranslationButtons
                                        collapsible={true}
                                        objectId={obj.id}
                                        objectType={translationObjectType}
                                        translations={obj.translations}
                                        activeLanguage={activeTranslation?.language}
                                        onTranslate={(language) => this.handleTranslate(obj.id, language, translationObjectType)}
                                        onClearTranslation={() => this.clearTranslation(obj.id)}
                                        loading={isTranslationLoading}
                                        loadingLanguage={loadingLanguage}
                                    />
                                </>}
                            </Likes>
                        </>}
                    </>}

                    {(imageLocation === 'right') && <>
                        {this.showImage({ obj, maxImageWidth, maxWidth, maxHeight, imageContainerStyles, imageStyles, hasVideo, imageLocation, currentListContainerClassname, youtubeLink })}
                    </>}

                    {obj[rightBoxField] && rightBoxFieldValue(obj[rightBoxField]) && <>
                        <div
                            class='box-line-right-box mt-1 mb-1 me-2 py-1 text-muted bg-light rounded-lg d-flex flex-column justify-content-center align-items-center flex-wrap text-center'
                            style='width: 60px !important; min-width: 60px !important; line-height: 1.0em;'
                        >
                            <span class='font-weight-bold'>{rightBoxFieldValue(obj[rightBoxField])}</span>
                            <small class='text-muted'>{rightBoxFieldPostfixValue ? rightBoxFieldPostfixValue(obj[rightBoxFieldPostfixField]) : rightBoxFieldPostfix}</small>
                            {rightBoxFieldBottomValue && <>
                                <small class='text-muted font-weight-lighter'>{rightBoxFieldBottomValue(obj)}</small>
                            </>}
                        </div>
                    </>}
                    {rightBoxTitle && <>
                        <div
                            class={`box-line-right-box mt-1 mb-1 me-2 py-1  rounded-lg d-flex flex-column justify-content-center align-items-center flex-wrap text-center
                                ${rightBoxClass && rightBoxClass(obj) ? rightBoxClass(obj) : 'text-muted bg-light'}`}
                            style='width: 60px; line-height: 1.0em;'
                        >
                            <span class='font-weight-bold'>{rightBoxValue(obj)}</span>
                            <small>{rightBoxTitle}</small>
                        </div>
                    </>}
                    {/* {obj.images && (showEmptyImage || obj.images[0] || obj.imageUrl) && <div class='box-line-image position-relative' style={`${imgHeight ? `height: ${imgHeight} !important;` : ''}`}>
                        {obj.images[0] ? <>
                            <img src={`${obj.images[0].s3MediumLink}`} class='rounded-lg' style={`${imgHeight ? `height: ${imgHeight} !important;` : ''}`} />
                        </> : <>
                            {obj.imageUrl ? <>
                                <img src={`${obj.imageUrl}`} class='rounded-lg' style={`${imgHeight ? `height: ${imgHeight} !important;` : ''}`} />
                            </> : <>
                                <div class='box-line-image-placeholder border h-100 rounded-lg d-flex justify-content-center align-items-center text-muted' style='font-size: 1.5em; opacity: 0.4;'>
                                    <i class='fa-solid fa-camera-retro' />
                                </div>
                            </>}
                        </>}
                        {opts.showInspections && <div class='box-line-status position-absolute' style='top: 5px; right: 5px;'>
                            {elImageStatus(obj)}
                        </div>}
                        {opts.allowEditQty && showIncDec(obj) && inc && <>
                            <button type='button' class='btn btn-lg btn-success px-1 py-0 position-absolute rounded-pill-both' style='font-size: 1.4em; bottom: 0px; right: 0px;' onClick={inc} data-id={obj.id} data-qty={obj.qty}><i class='fa-solid fa-plus' /></button>
                        </>}
                        {opts.allowEditQty && showIncDec(obj) && dec && <>
                            <button type='button' class='btn btn-lg btn-danger px-1 py-0 position-absolute rounded-pill-both' style='font-size: 1.4em;bottom: 0px; left: 0px;' onClick={dec} data-id={obj.id} data-qty={obj.qty}><i class='fa-solid fa-minus' /></button>
                        </>}
                    </div>} */}

                    {showCevron && <>
                        <div class='box-line-chevron mt-3 me-3 text-muted'>
                            <i class='fa-solid fa-chevron-right' />
                        </div>
                    </>}
                    {showRightIcon && <>
                        <div class='box-line-icon mt-3 me-3'>
                            <i class={showRightIcon(obj)} />
                        </div>
                    </>}
                    {/* {remove && <>
                        <button type='button' class='btn btn-lg btn-link' style='font-size: 1.5em;' onClick={remove} data-id={obj.id}><i class='fa-solid fa-trash' /></button>
                    </>} */}

                    {showComments && <>
                        <Comments stores={this.props.stores} type={obj._likeType || likeCommentType || 'article'} objectId={obj.id} comments={obj.comments} />
                    </>}
                </div>

                {showHeart[obj.id] && (
                    <div className='bigHeart'>
                        <svg viewBox='0 0 24 24'>
                            <path d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                                     C2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                                     C13.09 3.81 14.76 3 16.5 3
                                     C19.58 3 22 5.42 22 8.5
                                     c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' />
                        </svg>
                    </div>
                )}


            </div>
        </>);
    }

    searchFilter = (el) => {
        const { search } = this.state;

        const searchFields = ['name', 'title', 'firstname', 'lastname', 'sportsClub',
        'email', 'phone', 'address', 'city', 'zip', 'country', 'description', 'tags'];
        if (!search) {
            return true;
        }
        const searchLower = search.toLowerCase();
        for (let i = 0; i < searchFields.length; i += 1) {
            const field = searchFields[i];
            if (el[field] && `${el[field]}`.toLowerCase().includes(searchLower)) {
                return true;
            }
        }
        return false;
    }

    render() {
        const { search, focusedIndex } = this.state;
        const { appState, userStore } = this.props.stores;
        const { isDevelopment, path, apiServer } = appState;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const {
            category = '',
            title = '',
            list = [],
            classNames = '',
            styles = '',
            helpTextClassNames = '',
            hideEmpty = false,
            addItem,
            filter,
            sortTitle = '',
            sortBy = [],
            sortFunc = () => {},
            showFilterBox = true,
            toggle,
            toggleTitle,
            toggleFunc,
            help,
            showPadding = true,
            loadMore,
            listContainerClassnames = [],
            listWrapperClassNameFull,
            holdingStore,
            children,
            observeElement,
        } = this.props;

        if (hideEmpty && list.length === 0) {
            return (<></>);
        }
        // if (list.length === 0) {
        //     return (<>
        //         <div class={`w-100 d-flex flex-column`}>
        //             <Placeholder
        //                 count={1}
        //                 style={{
        //                     marginTop: '0.75em',
        //                     width: '100%',
        //                     height: '400px',
        //                 }}
        //             />
        //             <Placeholder
        //                 count={2}
        //                 style={{
        //                     marginTop: '0.75em',
        //                     marginBottom: '0.75em',
        //                     width: '100%',
        //                     height: '200px',
        //                 }}
        //             />
        //         </div>
        //     </>);
        // }

        let finalList = Array.isArray(list) ? list.filter(this.searchFilter) : [];

        if (sortBy.length > 0) {
            finalList = finalList.sort(fieldSorter([sortTitle]));
        }

        const sortByField = sortTitle.replace(/^-/, '');
        const sortByFieldObj = fields.getField('part', sortByField);

        const sortDir = sortTitle[0] === '-' ? 'desc' : 'asc';

        return (<>
            <div class={`w-100 d-flex flex-column ${classNames} position-relative`} style={styles}>
                {showFilterBox && list.length > 5 && <div class='d-flex flex-column justify-content-center px-3 mb-3'>
                    <div class='input-group'>
                        <div class='input-group-text rounded-pill-left'>
                            <i class='fa-regular fa-filter'></i>
                        </div>
                        <input
                            class={`form-control ${!search ? 'rounded-pill-right' : ''}`}
                            type='text'
                            value={search}
                            placeholder={this.props.filterPlaceholder.replace('{title}', title)}
                            onInput={this.searchInput}
                            onKeyDown={this.handleFilterKeyDown}
                            onFocus={(e) => e.target.select()}
                            ref={c => this.searchInputField = c}
                        />
                        {search && <>
                            <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                <i class='fa-solid fa-circle-xmark'></i>
                            </button>
                        </>}
                    </div>
                    {search && <>
                        <small class='text-muted mt-1 ms-2'>
                            <Text id='list.showing'>Showing</Text> {finalList.length} <Text id='list.of'>of</Text> {list.length} <Text id='list.results'>results</Text>
                        </small>
                    </>}
                </div>}

                {filter && filter() && <>
                    <div class='d-flex flex-row justify-content-start px-3 mb-3'>
                        <div class='text-muted'>
                            <Text id='list.filtered-by'>Filtered by:</Text>
                        </div>
                        <div class='flex-fill ms-2'>
                            {filter()}
                        </div>
                    </div>
                </>}

                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1'>
                    {title}
                    {children}
                    {addItem && <div class='float-right'>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={addItem}><i class='fa-solid fa-plus text-muted' /> <Text id='input.add'>Add</Text></button>
                    </div>}
                    {sortBy.length > 0 && sortFunc && <div class='float-right'>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill me-2' onClick={sortFunc}>
                            {sortDir === 'asc' ? <i class='fas fa-arrow-down-wide-short text-muted' /> : <i class='fas fa-arrow-up-short-wide text-muted' />} {sortByFieldObj ? sortByFieldObj.title : sortByField}
                        </button>
                    </div>}
                    {toggleTitle && toggleFunc && <div class='float-right'>
                        <button type='button' class={`btn btn-sm btn-${toggle ? 'success' : 'outline-secondary'} rounded-pill me-2`} onClick={toggleFunc}>
                            <i class='fas fa-filter' /> {toggleTitle}
                        </button>
                    </div>}
                </div>



                <div class={`d-flex flex-column ${showPadding ? 'mx-3' : ''}`}>
                    {list.length === 0 && <>
                        <div class='w-100 d-flex flex-column px-4 py-3 box-line'>
                            <div class='w-100 d-flex flex-row justify-content-center'>
                                <div class='box-line-add'>
                                    <i class='fa-solid fa-circle-question' /> {category}
                                </div>
                            </div>
                        </div>
                    </>}
                    <div class='d-flex flex-column' tabIndex='0' onKeyDown={this.handleKeyDown}>
                        <FastList
                            data={finalList}
                            containerWrapperClassNames={'w-100 d-flex flex-wrap'}
                            listWrapperClassNames={listContainerClassnames}
                            listWrapperClassNameFull={listWrapperClassNameFull}
                            wrapperClassNames={'overflow-hidden box-line'}
                            contentClassNames={`w-100 h-100 d-flex flex-row justify-content-between `}
                            renderContent={this.renderContent}
                            dataFields={['id', 'type']}
                            dataValues={{ type: holdingStore.name }}
                            handleIntersection={this.handleIntersection}
                            observeElement={observeElement}
                        />
                    </div>
                </div>
            </div>
            {help && <>
                <div
                    class={`w-100 d-flex justify-content-center px-3 text-center ${helpTextClassNames}`}
                    style='line-height: 1.0em;'
                >
                    <small class='text-muted'>
                        {help}
                    </small>
                </div>
            </>}
            {loadMore && <>
                <div class={`w-100 d-flex justify-content-center px-3 ${classNames}`}>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-secondary rounded-pill'
                        onClick={loadMore}
                    >
                        <i class='fas fa-arrow-down' /> <Text id='input.load-more'>Load more</Text>
                    </button>
                </div>
            </>}
        </>);
    }
}

export default GuiList;
